/**
 * useMapInteractions.ts
 * Base interaction handling for map
 */

import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { Map, MapMouseEvent, PaddingOptions } from "maplibre-gl";
import {
  Feature,
  FeatureCollection,
  Geometry,
  GeometryCollection
} from "geojson";

import { SUBLAYERS, SUFFIX_LAYER_BVARTA } from "@/constants/map";
import { store } from "@/store";
import {
  addComparisonFeature,
  setSelectedFeature
} from "@/store/slice/mapSlice";
import { toggleComparisonLimitShow } from "@/store/slice/uiSlice";
import { AdminAreaData } from "@/types/adminArea";
import { BoundingBox, SelectedFeature } from "@/types/map";
import { calculateFeatureBounds } from "@/utils/common";

import { bbox, featureCollection, pointOnFeature } from "@turf/turf";

import { DEFAULT_ZOOM } from "../core/useMapInitialization";
import { findClickableFeature, isFeatureAlreadyActive } from "./helpers";

export const BOUNDS_PADDING = 50; // General padding in pixels to prevent features from rendering too close to the edge, especially on high-DPI or small screens
export const MAX_ZOOM = 16;
export const TO_BOUNDS_DURATION = 1000; // milliseconds;

// Extended Feature type that includes maplibre-specific properties
interface MapFeature extends Feature<Geometry> {
  source?: string;
  id?: string | number;
}

type UseMapInteractionsProps = {
  map: Map;
  handleClearSelectedState: (feature: SelectedFeature) => void;
};
/**
 * Hook for managing map interactions
 */
export const useMapInteractions = ({
  map,
  handleClearSelectedState
}: UseMapInteractionsProps) => {
  const dispatch = useDispatch();

  // Layer ids already bound to the click handler, so we never bind twice.
  // Tracked alongside the map the set belongs to: a new map instance starts
  // with no listeners, so the set is reset lazily when the map identity changes.
  const boundClickRef = useRef<{ map: Map | null; layers: Set<string> }>({
    map: null,
    layers: new Set()
  });

  const handleMovePositionToLayer = (
    polygonData: FeatureCollection<Geometry>,
    options?: {
      padding?: PaddingOptions | number;
      offset?: [number, number]; // [x, y] offset in pixels
      zoom?: number; // Custom zoom level
    }
  ) => {
    if (!map) return;

    const bounds = calculateFeatureBounds(polygonData);

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: options?.padding ?? BOUNDS_PADDING,
        maxZoom: options?.zoom ?? MAX_ZOOM,
        duration: TO_BOUNDS_DURATION,
        ...(options?.offset && { offset: options.offset })
      });
    }
  };

  const clickHandler = (
    e: MapMouseEvent & {
      features?: MapFeature[];
    }
  ) => {
    const features = map.queryRenderedFeatures(e.point);

    if (!map || !e.features || e.features.length === 0) return;

    // Get the first clickable feature in map if the feature is overlayed by other features
    // E.g. A POI point should be the clicked feature from the map when it gets overlayed by admin area, catchment radius, or isochrone radius
    const clickedFeature = findClickableFeature(features);

    if (!clickedFeature) return;

    const currentState = store.getState();
    const { selectedFeature, comparisonFeatureData } = currentState.map;
    const { showComparisonPopup } = currentState.ui;

    if (showComparisonPopup.isShow) {
      handleComparisonModeClick(
        clickedFeature,
        comparisonFeatureData,
        selectedFeature
      );
      return;
    }

    handleNormalModeClick(clickedFeature, selectedFeature);
  };

  const handleComparisonModeClick = (
    feature: MapFeature,
    comparisonFeatures: SelectedFeature[],
    currentSelection: SelectedFeature | null
  ) => {
    if (feature.id == null) return;

    // Early return: feature already selected
    if (isFeatureAlreadyActive(feature, currentSelection, comparisonFeatures)) {
      return;
    }

    // Early return: limit reached
    if (comparisonFeatures.length > 3) {
      dispatch(toggleComparisonLimitShow());
      return;
    }

    // Add to comparison
    map.setFeatureState(
      { source: feature.source as string, id: feature.id },
      { selected: true }
    );

    dispatch(
      addComparisonFeature({
        properties: feature.properties,
        id: feature.id ?? "",
        source: feature.source ?? "",
        geometry: feature.geometry as Exclude<Geometry, GeometryCollection>
      })
    );
  };

  const handleNormalModeClick = (
    feature: MapFeature,
    currentSelection: SelectedFeature | null
  ) => {
    if (feature.id == null) return;

    // Clear previous selection if exists
    if (currentSelection?.id === 0 || !!currentSelection?.id) {
      handleClearSelectedState(currentSelection);
    }

    // Set new selection
    map.setFeatureState(
      { source: feature.source as string, id: feature.id },
      { selected: true }
    );

    dispatch(
      setSelectedFeature({
        properties: feature.properties,
        id: feature.id ?? "",
        source: feature.source ?? "",
        geometry: feature.geometry as Exclude<Geometry, GeometryCollection>
      })
    );
  };

  // FIX ME for use cleanup pattern
  const handleFeatureClick = (layerId: string): void => {
    if (!map) return;

    const bound = boundClickRef.current;
    // Reset tracking when the map instance changes — the new map has no listeners.
    if (bound.map !== map) {
      bound.map = map;
      bound.layers = new Set();
    }

    if (bound.layers.has(layerId)) return;
    // A delegated click listener registered via map.on("click", layerId, fn)
    // survives map.removeLayer(), so re-adding a layer with the same id (e.g.
    // reopening a saved analysis) would stack a second listener and fire the
    // handler twice. Bind each layer only once — the surviving listener keeps
    // working after the layer is re-added.
    bound.layers.add(layerId);
    map.on("click", layerId, clickHandler);
  };

  const fitMapToLayerBound = useCallback(
    (items: AdminAreaData) => {
      if (!map) return;

      if (items?.data?.length) {
        const allFeatures = items.data.flatMap(
          (item) => item.boundary.features
        );
        const mergedFeatureCollection = featureCollection(allFeatures);

        const combinedBbox = bbox(mergedFeatureCollection);

        map.fitBounds(combinedBbox as BoundingBox, {
          padding: BOUNDS_PADDING
        });
      }
    },
    [map]
  );

  /**
   * Resets the map view to its default orientation (bearing: 0, pitch: 0) and default zoom level (4),
   * while preserving the current center position.
   */
  const resetMapView = () => {
    if (!map) return;

    map.flyTo({
      zoom: DEFAULT_ZOOM,
      duration: TO_BOUNDS_DURATION,
      bearing: 0,
      pitch: 0
    });
  };

  const resetAllFeatureStates = useCallback(() => {
    if (!map) return;

    const style = map.getStyle();
    if (!style?.sources) return;

    for (const [sourceId, source] of Object.entries(style.sources)) {
      if (source.type === "geojson") {
        map.removeFeatureState({ source: sourceId });
      }
    }
  }, [map]);

  const handleHighlightFeatureByGeometry = useCallback(
    (sourceId: string, geometry: Geometry) => {
      if (!map || !geometry || !sourceId) return;
      // remove previous selection
      const currentState = store.getState();
      const { selectedFeature } = currentState.map;

      if (selectedFeature?.id === 0 || !!selectedFeature?.id) {
        handleClearSelectedState(selectedFeature);
      }

      const insidePoint = pointOnFeature(geometry).geometry.coordinates;
      const [lng, lat] = insidePoint;

      const geometryBounds = bbox(geometry) as BoundingBox;

      map.fitBounds(geometryBounds, {
        padding: BOUNDS_PADDING,
        duration: TO_BOUNDS_DURATION
      });
      const onIdle = () => {
        const pixelPoint = map.project([lng, lat]);
        const features = map.queryRenderedFeatures(pixelPoint);
        const target =
          features.find((f) => f.source === sourceId) ?? features[0];
        if (!target) return;

        const featureId = target.id;
        const getAllSublayerSources = (datasetId: string) => {
          const config = SUBLAYERS(datasetId);
          return Object.values(config).flatMap((group) => Object.values(group));
        };

        const possibleSources = getAllSublayerSources(sourceId);

        const baseSource = possibleSources.includes(target.source)
          ? target.source
          : sourceId;

        if (featureId !== undefined) {
          map.setFeatureState(
            { source: baseSource, id: featureId },
            { selected: true }
          );
        }

        dispatch(
          setSelectedFeature({
            properties: target.properties,
            id: target.id ?? "",
            source: target.source ?? "",
            geometry: target.geometry as Exclude<Geometry, GeometryCollection>
          })
        );
      };
      map.once("moveend", onIdle);
      map.once("idle", onIdle);
    },
    [dispatch, handleClearSelectedState, map]
  );

  useEffect(() => {
    if (!map) return;

    const handleMapClick = (e: MapMouseEvent) => {
      const features = map.queryRenderedFeatures(e.point);
      const clickedFeature = findClickableFeature(features);

      // Global click handler to clear selection when clicking outside of features
      // Clear on feature change is already handled by handleNormalModeClick
      if (
        clickedFeature &&
        !SUFFIX_LAYER_BVARTA.some((suffix) =>
          clickedFeature?.layer?.id?.endsWith(`-${suffix}`)
        )
      ) {
        const selectedFeature = store.getState().map.selectedFeature;
        if (selectedFeature?.id === 0 || !!selectedFeature?.id) {
          handleClearSelectedState(selectedFeature);
        }
      }
    };

    map.on("click", handleMapClick);

    return () => {
      map.off("click", handleMapClick);
    };
  }, [handleClearSelectedState, map]);

  return {
    handleHighlightFeatureByGeometry,
    fitMapToLayerBound,
    handleFeatureClick,
    handleMovePositionToLayer,
    resetAllFeatureStates,
    resetMapView
  };
};

export default useMapInteractions;
